import AppModule from '@app/lib/AppModule';
import { tasksState } from './state';
import { CreateTaskOptions, TaskState } from './types';
import Task from './Task';
import appManager from '@app/appManager';
import ToastModule from '@modules/ToastModule';
import getClassificationTypeName from './lib/getClassificationTypeName';
import { InteractionChannel } from '@api/generated/graphql';
import { showCloseAndClassifyErrorToast } from '@modules/ToastModule/commonToasts/commonToasts';

// THIS MODULE IS A WORK IN-PROGRESS A WILL BE REFACTORED
// SEE DAVEW BEFORE MAKING MAJOR CHANGES

function getTaskEvents(): string[] {
  return [
    'task.pending',
    'task.offered',
    'task.rejected',
    'task.abandoned',
    'task.accepted',
    'task.wrapup',
    'task.completed',
    'task.timedout',
    'task.updated',
    'task.action.accept',
    'task.action.reject',
    'task.action.complete',
    'task.action.complete-classified',
    'task.action.unhold',
  ];
}

enum ClassificationRequiredCallTypes {
  'Unbooked' = 'Unbooked',
  'Excused' = 'Excused',
}

const EXCUSED_DURATION_REQUIRING_CLASSIFICATION_MS = 60_000;

export class TasksModule extends AppModule {
  override events = [...getTaskEvents()];

  override preload(): void {
    this.app.events.on('auth.signout', () => {
      this.handleClearTasks();
    });
  }

  public createTask(options: CreateTaskOptions): Task {
    this.app.logger.info('tasks:create', options);
    const newTask = new Task(options);
    tasksState.tasks[newTask.id] = newTask;
    return tasksState.tasks[newTask.id];
  }

  /**
   * In order for us to populate the task with tenantId and the customer name, we need to create one before we place the call.
   * Once the outbound call is made, we will try to find the task with the same number and update the task with the sessionId.
   * Handled in `setOutboundCallToOffered`.
   */
  public createOutboundCallTask(
    outgoingNumber: string,
    displayName?: string,
    tenantId?: string,
  ) {
    return this.createTask({
      isInternal: true,
      id: `tmp-outbound-${outgoingNumber}`,
      from: outgoingNumber,
      displayName,
      direction: 'outbound',
      type: 'voice',
      state: TaskState.Pending,
      tenantId,
    });
  }

  // This method is used to update the task id when the outbound call is made. Since we create it with
  // a temporary session id (`tmp-outbound`), we need to update it with the actual session id when the
  // call is placed.
  public updateTaskId(task: Task, newSessionId: string) {
    tasksState.tasks[newSessionId] = task;
    this.deleteTask(task.id);
    tasksState.tasks[newSessionId].id = newSessionId;
  }

  public setTaskState(sessionId: string, taskState: TaskState) {
    const task = tasksState.tasks[sessionId];
    if (!task) {
      this.app.logger.error('task not found', sessionId);
      return;
    }
    task.state = taskState;
  }

  public setTaskAttributes(taskId: string, attributes: Record<string, any>) {
    const task = tasksState.tasks[taskId];
    if (!task) {
      throw new Error(`Task not found: ${taskId}`);
    }
    task.attributes = attributes;
  }

  public setTaskAttribute(taskId: string, key: string, value: any) {
    const task = tasksState.tasks[taskId];
    this.setTaskAttributes(taskId, {
      ...task.attributes,
      [key]: value,
    });
  }

  public setTaskDisplayName(taskId: string, displayName: string | undefined) {
    const task = tasksState.tasks[taskId];
    if (!task) {
      throw new Error(`Task not found: ${taskId}`);
    }
    task.displayName = displayName;
  }

  public setTaskComplete(taskId: string) {
    const task = tasksState.tasks[taskId];
    if (!task) {
      console.error('task not found', taskId);
      return;
    }
    task.state = TaskState.Completed;
    this.deleteTask(taskId);
  }

  public deleteTask(taskId: string) {
    const task = tasksState.tasks[taskId];
    delete tasksState.tasks[taskId];

    ToastModule.hideIncomingCallToast(task.id);
  }

  public actionTask(
    taskId: string,
    action: 'accept' | 'reject' | 'complete' | 'complete-classified' | 'unhold',
  ) {
    const task = tasksState.tasks[taskId];
    if (!task) {
      console.error('task not found', taskId);
      return;
    }
    this.app.events.emit(`task.action.${action}`, task);
  }

  public getTask(taskId: string): Task | null {
    return tasksState.tasks[taskId] || null;
  }

  public getTasks(): Task[] {
    return Object.values(tasksState.tasks);
  }

  // This method is used to find the outbound call task by the from number and return the most recent one
  public findOutboundCallTask(outgoingNumber: string): Task | null {
    const outboundTasksToNumber = Object.values(tasksState.tasks)
      .filter(
        (task) =>
          task.state === TaskState.Pending &&
          task.type === 'voice' &&
          task.direction === 'outbound' &&
          task.from === outgoingNumber,
      )
      .sort((a, b) => a.startedAt.getTime() - b.startedAt.getTime());

    return outboundTasksToNumber.pop() || null;
  }

  // Find task by pcsConversationId
  public findTaskByPcsConversationSid(
    pcsConversationSid: string,
  ): Task | undefined {
    return Object.values(tasksState.tasks).find(
      (task) => task.id === pcsConversationSid,
    );
  }

  public async checkClassificationRequired(
    taskId: string,
  ): Promise<boolean | null> {
    const task = this.getTask(taskId);
    if (!task) {
      console.error('task not found for sessionId:', taskId);
      return null;
    }
    task.loading = true;
    const classificationType = await getClassificationTypeName(
      task.tenantId,
      task.id,
      InteractionChannel.InboundVoice,
      this.app.logger,
    );

    task.loading = false;

    if (classificationType === null) {
      // classification check errored or is not available, do nothing.
      this.app.logger.info('classification not available', taskId);
      showCloseAndClassifyErrorToast();
      return null;
    }

    // if it is not unbooked or excused, then classification is not required
    if (
      !Object.values(ClassificationRequiredCallTypes)
        .map((type) => type.toString())
        .includes(classificationType)
    ) {
      this.app.logger.info('classification not required', taskId);
      return false;
    }

    if (classificationType === ClassificationRequiredCallTypes.Unbooked) {
      this.app.logger.info(
        `call type: ${classificationType}, classification required`,
        taskId,
      );
      return true;
    }

    const startedAt = task.startedAt.valueOf();

    const durationSeconds = (task.wrapUpStartedAt ?? 0) - startedAt;

    if (
      classificationType === ClassificationRequiredCallTypes.Excused &&
      durationSeconds < EXCUSED_DURATION_REQUIRING_CLASSIFICATION_MS
    ) {
      this.app.logger.info(
        `call type: ${classificationType}, duration < 60s, classification is required`,
        taskId,
      );
      return true;
    } else if (
      classificationType === ClassificationRequiredCallTypes.Excused &&
      durationSeconds >= EXCUSED_DURATION_REQUIRING_CLASSIFICATION_MS
    ) {
      this.app.logger.info(
        `call type: ${classificationType}, duration > 60s, classification not required`,
        taskId,
      );
      return false;
    }

    showCloseAndClassifyErrorToast();
    this.app.logger.error('unhandled classification', {
      task,
      classificationType,
      durationSeconds,
    });
    return null;
  }

  public setTaskLoading = (taskId: string, loading: boolean) => {
    const task = tasksState.tasks[taskId];
    if (!task) {
      throw new Error(`Task not found: ${taskId}`);
    }
    task.loading = loading;
  };

  private handleClearTasks = () => {
    tasksState.tasks = {};
  };
}

export default new TasksModule(appManager.events, appManager.logger('Tasks'));
